import { omit } from '@fxts/core';
import { ShopProductListUrlOption } from '../type';
import { updateUrlParams } from '../../../shared/util';
import { PROFIT_EXCHANGE_RATE_EN, PROFIT_EXCHANGE_RATE_JP } from '@modules/Price/S/Constant/exchange_rate';

export enum ShopPage {
  // 쇼핑 기본 페이지
  default = 'default',

  // 전체 상품
  all_products = 'all_products',

  // 실시간 팔린 상품
  recently_sold = 'recently_sold',

  // 랭킹
  ranking = 'ranking',
}

/**
 * 상품 목록의 url을 만드는 함수입니다.
 *
 * @note
 * `ShopPage.ranking`은 아직 미지원합니다.
 *
 *
 * @todo 이렇게 리팩터링하기 (서브페이지도 할당할 수 있게 .. )
 * ```ts
 * export class ProductUrlGenerator {
 *   static readonly ShopPage = ShopPage;
 *
 *   constructor(private shopPage: ShopPage) {}
 *
 *   setPage(shopPage: ShopPage) {
 *     this.shopPage = shopPage;
 *   }
 *
 *   setMenu(menu: Menu) {}
 * }
 * ```
 */
export const generateProductListUrl = (option?: ShopProductListUrlOption) => {
  // 아무 프로퍼티도 없을 때는 "전체 상품"으로
  if (!option) {
    option = {
      // 전체상품일 때 기본 값은 품절 제외
      without_sold_out: true,
      page: ShopPage.all_products,
    };
  }

  // todo @kjj: ranking url 제너레이터랑 합치기
  if (option.page === ShopPage.ranking) {
    console.warn('ShopPage.ranking 은 아직 지원되지 않습니다.');
    option.page = ShopPage.default;
  }

  const pageMap: Record<ShopPage, string> = {
    [ShopPage.default]: `/${ET.lang}/@/realtime`,
    [ShopPage.all_products]: `/${ET.lang}/@/products`,
    [ShopPage.recently_sold]: `/${ET.lang}/@/realtime`,
    [ShopPage.ranking]: '',
  };

  const base_url = option.base_url ?? pageMap.all_products;

  const {
    is_limited,
    without_sold_out,
    creator_filter_ids = [],
    style_filter_ids = [],
    is_realtime,
  } = option;

  if (is_realtime || option.page === ShopPage.recently_sold) {
    return pageMap.recently_sold;
  }

  if (option.page === ShopPage.default) {
    return pageMap.default;
  }

  return updateUrlParams(base_url, {
    ...omit(['base_url', 'is_realtime', 'page'], option),
    style_filter_ids: style_filter_ids.length ? style_filter_ids.join(',') : undefined,
    creator_filter_ids: creator_filter_ids.length ? creator_filter_ids.join(',') : undefined,
    is_limited: is_limited ? 'true' : undefined,
    without_sold_out: without_sold_out ? 'true' : undefined,
  });
};

/**
 * "플러스샵" 상품 목록의 url을 만드는 함수입니다.
 */
export const generatePlusShopProductListUrl = (
  option: {
    is_limited?: boolean;
    without_sold_out?: boolean;
    is_best?: boolean;
    type?: 'best' | 'limited';
  } = {},
) => {
  const BASE_URL = `/${ET.lang}/@/products`;
  const { is_limited, without_sold_out, type } = option;

  // 베스트
  if (type === 'best') {
    return updateUrlParams(BASE_URL, { type: 'best' }, false);
  }

  // 한정판
  if (type === 'limited') {
    return updateUrlParams(BASE_URL, { type: 'limited' }, false);
  }

  return updateUrlParams(BASE_URL, {
    is_limited: is_limited ? 'true' : undefined,
    without_sold_out: without_sold_out ? 'true' : undefined,
  });
};

export const getMaxPriceByLanguage = (lang: 'kr' | 'en' | 'jp'): number => {
  const MAX_PRICE_KR = 1_000_000;
  if (lang === 'en') return Math.ceil(MAX_PRICE_KR * PROFIT_EXCHANGE_RATE_EN);
  if (lang === 'jp') return Math.ceil(MAX_PRICE_KR * PROFIT_EXCHANGE_RATE_JP);
  return MAX_PRICE_KR;
};
